/**
 * Maintenance mode configuration interface
 * Uses time-based windows (startedAt/endedAt) instead of a simple boolean
 */
export interface MaintenanceConfig {
    startedAt: string; // ISO 8601 timestamp when maintenance starts
    endedAt: string;   // ISO 8601 timestamp when maintenance ends
    message?: string;  // Optional custom message
}

/**
 * Service for checking maintenance mode from external source
 * This allows toggling maintenance mode without redeploying the app
 * Uses time-based windows to schedule maintenance ahead of time
 */
class MaintenanceService {
    private config: MaintenanceConfig | null = null;
    private loadingPromise: Promise<MaintenanceConfig | null> | null = null;
    private lastCheckTime: number = 0;
    private readonly CACHE_DURATION = 30000; // Cache for 30 seconds (matches polling interval)
    private readonly CHECK_TIMEOUT = 5000; // 5 second timeout

    /**
     * Get the maintenance mode configuration URL
     * Must be set via environment variable VITE_MAINTENANCE_CONFIG_URL
     */
    private getMaintenanceUrl(): string {
        const envUrl = import.meta.env.VITE_MAINTENANCE_CONFIG_URL;
        if (!envUrl) {
            throw new Error('VITE_MAINTENANCE_CONFIG_URL environment variable is required. Maintenance mode configuration must be hosted in Azure Blob Storage.');
        }
        return envUrl;
    }

    /**
     * Check if maintenance mode is currently active based on time windows
     * Returns true if current time is between startedAt and endedAt
     */
    isMaintenanceActive(config: MaintenanceConfig | null): boolean {
        if (!config) {
            return false;
        }

        try {
            const now = new Date();
            const startTime = new Date(config.startedAt);
            const endTime = new Date(config.endedAt);

            // Validate dates
            if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
                console.warn('[MaintenanceService] Invalid date format in maintenance config');
                return false;
            }

            // Check if we're in the maintenance window
            return now >= startTime && now < endTime;
        } catch (error) {
            console.error('[MaintenanceService] Error checking maintenance window:', error);
            return false;
        }
    }

    /**
     * Get time until maintenance starts (if scheduled for future)
     * Returns null if maintenance is active or not scheduled
     */
    getTimeUntilStart(config: MaintenanceConfig | null): number | null {
        if (!config) {
            return null;
        }

        try {
            const now = new Date();
            const startTime = new Date(config.startedAt);

            if (isNaN(startTime.getTime())) {
                return null;
            }

            // If maintenance hasn't started yet, return milliseconds until start
            if (now < startTime) {
                return startTime.getTime() - now.getTime();
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Get time until maintenance ends (if currently active)
     * Returns null if maintenance is not active
     */
    getTimeUntilEnd(config: MaintenanceConfig | null): number | null {
        if (!config) {
            return null;
        }

        try {
            const now = new Date();
            const endTime = new Date(config.endedAt);

            if (isNaN(endTime.getTime())) {
                return null;
            }

            // If maintenance is active, return milliseconds until end
            if (now >= new Date(config.startedAt) && now < endTime) {
                return endTime.getTime() - now.getTime();
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Check if maintenance mode is enabled
     * Uses caching to avoid excessive requests
     */
    async checkMaintenanceMode(): Promise<MaintenanceConfig | null> {
        const now = Date.now();

        // Return cached config if still valid
        if (this.config && (now - this.lastCheckTime) < this.CACHE_DURATION) {
            return this.config;
        }

        // Return existing loading promise if already loading
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        // Start loading
        this.loadingPromise = this.fetchMaintenanceConfig();

        try {
            const config = await this.loadingPromise;
            this.config = config;
            this.lastCheckTime = now;
            this.loadingPromise = null;
            return config;
        } catch (error) {
            this.loadingPromise = null;
            // If fetch fails, assume maintenance mode is disabled
            // This ensures the app works even if the maintenance file is unavailable
            console.debug('[MaintenanceService] Failed to fetch maintenance config, assuming disabled:', error);
            this.config = null;
            this.lastCheckTime = now;
            return null;
        }
    }

    /**
     * Fetch maintenance configuration from external source
     */
    private async fetchMaintenanceConfig(): Promise<MaintenanceConfig | null> {
        const baseUrl = this.getMaintenanceUrl();
        // Add cache-busting query parameter to prevent browser caching
        const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.CHECK_TIMEOUT);

            const response = await fetch(url, {
                method: 'GET',
                cache: 'no-store', // Use 'no-store' instead of 'no-cache' for stronger cache prevention
                headers: {
                    'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            // If file doesn't exist (404), maintenance mode is disabled
            if (response.status === 404) {
                return null;
            }

            if (!response.ok) {
                throw new Error(`Failed to fetch maintenance config: ${response.status}`);
            }

            // Check if response is actually JSON (not HTML from 404 override)
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                // Got HTML instead of JSON, assume disabled
                return null;
            }

            const config: MaintenanceConfig = await response.json();

            // Validate the response has required fields
            if (!config.startedAt || !config.endedAt) {
                console.warn('[MaintenanceService] Invalid maintenance config (missing startedAt or endedAt), assuming disabled');
                return null;
            }

            // Validate dates are valid
            const startTime = new Date(config.startedAt);
            const endTime = new Date(config.endedAt);
            if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
                console.warn('[MaintenanceService] Invalid date format in maintenance config, assuming disabled');
                return null;
            }

            // Validate end time is after start time
            if (endTime <= startTime) {
                console.warn('[MaintenanceService] endedAt must be after startedAt, assuming disabled');
                return null;
            }

            return config;
        } catch (error) {
            // Network errors, timeouts, etc. - assume maintenance mode is disabled
            if (error instanceof Error && error.name === 'AbortError') {
                console.debug('[MaintenanceService] Maintenance check timed out, assuming disabled');
            }
            throw error;
        }
    }

    /**
     * Clear the cache to force a fresh check
     */
    clearCache(): void {
        this.config = null;
        this.lastCheckTime = 0;
    }
}

// Export singleton instance
export const maintenanceService = new MaintenanceService();

